import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import Img from 'gatsby-image'
import { Box, Text, Card, Flex, Button } from 'theme-ui'
import { Stack, Main, Hero } from '@layout'
import PageTitle from '@components/PageTitle'
import Divider from '@components/Divider'
import Section from '@components/Section'
import Seo from '@widgets/Seo'
import HeroComponent from '../components/Hero/Hero'
import HeroQuoteComponent from '../components/HeroQuote/Hero'
import ContactExpanded from '@widgets/ContactExpanded'
import CardList from '@components/CardList'

const styles = {
  imageWrapper: {
    borderRadius: `default`,
    overflow: `hidden`,
    position: `relative`
  },
  button: {
    display: [`none`, `block`],
    position: `absolute`,
    bottom: 4,
    right: 4
  },
  grid: {
    flexWrap: [`wrap`, `wrap`, `nowrap`],
    ' > div + div': {
      ml: [0, 0, 5]
    }
  },
  column: {
    flex: `auto`,
    flexBasis: [`full`, `full`, `1/2`]
  },
  hero: {
    // bg: `contentBg`,
    backgroundImage:
      'linear-gradient(231deg, rgba(233, 233, 233, 0.01) 0%, rgba(233, 233, 233, 0.01) 25%,rgba(10, 10, 10, 0.01) 25%, rgba(10, 10, 10, 0.01) 50%,rgba(237, 237, 237, 0.01) 50%, rgba(237, 237, 237, 0.01) 75%,rgba(200, 200, 200, 0.01) 75%, rgba(200, 200, 200, 0.01) 100%),linear-gradient(344deg, rgba(2, 2, 2, 0.03) 0%, rgba(2, 2, 2, 0.03) 20%,rgba(10, 10, 10, 0.03) 20%, rgba(10, 10, 10, 0.03) 40%,rgba(100, 100, 100, 0.03) 40%, rgba(100, 100, 100, 0.03) 60%,rgba(60, 60, 60, 0.03) 60%, rgba(60, 60, 60, 0.03) 80%,rgba(135, 135, 135, 0.03) 80%, rgba(135, 135, 135, 0.03) 100%),linear-gradient(148deg, rgba(150, 150, 150, 0.03) 0%, rgba(150, 150, 150, 0.03) 14.286%,rgba(15, 15, 15, 0.03) 14.286%, rgba(15, 15, 15, 0.03) 28.572%,rgba(74, 74, 74, 0.03) 28.572%, rgba(74, 74, 74, 0.03) 42.858%,rgba(175, 175, 175, 0.03) 42.858%, rgba(175, 175, 175, 0.03) 57.144%,rgba(16, 16, 16, 0.03) 57.144%, rgba(16, 16, 16, 0.03) 71.42999999999999%,rgba(83, 83, 83, 0.03) 71.43%, rgba(83, 83, 83, 0.03) 85.71600000000001%,rgba(249, 249, 249, 0.03) 85.716%, rgba(249, 249, 249, 0.03) 100.002%),linear-gradient(122deg, rgba(150, 150, 150, 0.01) 0%, rgba(150, 150, 150, 0.01) 20%,rgba(252, 252, 252, 0.01) 20%, rgba(252, 252, 252, 0.01) 40%,rgba(226, 226, 226, 0.01) 40%, rgba(226, 226, 226, 0.01) 60%,rgba(49, 49, 49, 0.01) 60%, rgba(49, 49, 49, 0.01) 80%,rgba(94, 94, 94, 0.01) 80%, rgba(94, 94, 94, 0.01) 100%),linear-gradient(295deg, rgba(207, 207, 207, 0.02) 0%, rgba(207, 207, 207, 0.02) 25%,rgba(47, 47, 47, 0.02) 25%, rgba(47, 47, 47, 0.02) 50%,rgba(142, 142, 142, 0.02) 50%, rgba(142, 142, 142, 0.02) 75%,rgba(76, 76, 76, 0.02) 75%, rgba(76, 76, 76, 0.02) 100%),linear-gradient(73deg, rgba(81, 81, 81, 0.03) 0%, rgba(81, 81, 81, 0.03) 12.5%,rgba(158, 158, 158, 0.03) 12.5%, rgba(158, 158, 158, 0.03) 25%,rgba(136, 136, 136, 0.03) 25%, rgba(136, 136, 136, 0.03) 37.5%,rgba(209, 209, 209, 0.03) 37.5%, rgba(209, 209, 209, 0.03) 50%,rgba(152, 152, 152, 0.03) 50%, rgba(152, 152, 152, 0.03) 62.5%,rgba(97, 97, 97, 0.03) 62.5%, rgba(97, 97, 97, 0.03) 75%,rgba(167, 167, 167, 0.03) 75%, rgba(167, 167, 167, 0.03) 87.5%,rgba(22, 22, 22, 0.03) 87.5%, rgba(22, 22, 22, 0.03) 100%),linear-gradient(90deg, hsl(137,0%,23%),hsl(137,0%,23%))'
  },
  link: {
    a: {
      variant: `links.normal`
    }
  },
  quickLinks: {
    textAlign: ['center', 'unset']
  }
}

export default props => {
  const data = useStaticQuery(homepageQuery)
  const { recentPosts } = data

  return (
    <>
      <Seo
        title="Viadog"
        description="We build internal tools for startups and small businesses."
        siteUrl="//www.viadog.com"
        thumbnail="/ViadogImage.png"
      />
      <Hero sx={styles.hero}>
        <HeroComponent {...props} />
      </Hero>
      <Divider />
      <Stack>
        <Main>
          <Flex sx={styles.grid}>
            <Box sx={styles.column}>
              <Section title='What We Do'>
                <Text variant='p'>
                  We hate repetitive manual tasks just as much as you do. Our
                  focus is to automate the boring stuff to allow you to spend
                  your time on the more important things within your business.
                </Text>
                <Text variant='p'>
                  In a fast paced startup where time is your scarcest resource,
                  we've found automation is key to moving forward.
                </Text>
                <Text variant='p'>
                  Some of the things we work on:
                  <p>
                    <strong>Dashboards:</strong> A place to centrally monitor
                    and control all aspects of your business.
                  </p>
                  <p>
                    <strong>Microservices:</strong> Small, independant functions
                    which focus on solving one problem at a time.
                  </p>
                  <p>
                    <strong>Web apps:</strong> From MVPs (Minimum Viable
                    Product) to full featured applications.
                  </p>
                </Text>
                <Text variant='p' sx={styles.link}>
                  We currently focus within ecommerce and telecoms industries
                  however if you have a project you think we'd be a good fit
                  for, please <Link to='/contact'>get in touch</Link>.
                </Text>
              </Section>
            </Box>
            <Box sx={styles.column}>
              <Section title='Shopify & ReCharge User?'>
                <Card variant='paper'>
                  <p>
                    We've made it as simple as possible to get more value out of
                    your Shopify and ReCharge subscription orders.
                  </p>
                  <p>
                    Increase revenue and reduce churn with our set and forget
                    tools.
                  </p>

                  <Box variant='buttons.group' sx={styles.quickLinks}>
                    <Button variant='primary' as={Link} to='/subscriptions'>
                      Learn More
                    </Button>
                  </Box>
                </Card>
              </Section>
              {/*<Divider />
              <Section title='Recent Posts'>
                <Card variant='paper'>
                  123
                </Card>
              </Section>*/}
            </Box>
          </Flex>
        </Main>
      </Stack>
      {/*<Divider />
      <Hero sx={{ bg: `contentBg` }}>
        <HeroQuoteComponent />
      </Hero>*/}
      <Divider space={5} />
      <Stack>
        <Main>
          <ContactExpanded />
        </Main>
      </Stack>
      {/*<Divider space={5} />
      <Stack>
        <Main>
          <Section title='Recent Posts'>
            <CardList
              nodes={recentPosts.nodes}
              // variant={['vertical-cover']}
              // limit={2}
              // variant='horizontal-aside'
              // variant='horizontal-md'
              // variant='horizontal-lg'
              // variant='horizontal'
              // variant='vertical'
              // variant='horizontal-cover'
              // variant='horizontal-cover-hero'
              // title='Recent Posts'
              // columns={[3]}
              // omitMedia
              // omitCategory
              // aside
              limit={3}
              columns={[1, 3]}
              variant={['horizontal-md', 'vertical-cover']}
            />
          </Section>
        </Main>
    </Stack>*/}
    </>
  )
}

const homepageQuery = graphql`
  query HomepageQuery {
    recentPosts: allArticle(
      filter: { private: { ne: true } }
      sort: { fields: [date], order: DESC }
      limit: 3
    ) {
      nodes {
        ...ArticlePreview
        ...ArticleThumbnailRegular
      }
    }
  }
`
